// Stable, user-facing application error contract.
//
// `message` explains what failed. `action` tells the operator what to do next.
// `code` is stable across releases and is the key stored in action logs.
// `requestId` correlates the browser report with structured server logs.

export const ERROR_DEFINITIONS = Object.freeze({
  AUTH_REQUIRED: {
    title: 'Sign-in required',
    message: 'Your session is missing or has expired.',
    action: 'Sign in again, then retry the action.',
    retryable: true,
  },
  FORBIDDEN: {
    title: 'Permission denied',
    message: 'Your account is not allowed to perform this action.',
    action: 'Ask a customer administrator to check your role and access.',
    retryable: false,
  },
  NOT_FOUND: {
    title: 'Item not found',
    message: 'The requested item no longer exists or is not available to you.',
    action: 'Refresh the page. If it should still exist, contact an administrator.',
    retryable: false,
  },
  UPLOAD_FILE_TOO_LARGE: {
    title: 'File too large',
    message: 'One of the selected files exceeds the server upload limit.',
    action: 'Use a smaller file, split the dataset, or ask an administrator to raise the limit.',
    retryable: false,
  },
  UPLOAD_TOO_MANY_FILES: {
    title: 'Too many files',
    message: 'The upload contains more files than the server accepts in one request.',
    action: 'Upload a ZIP archive or split the dataset into smaller batches.',
    retryable: false,
  },
  UPLOAD_TOTAL_TOO_LARGE: {
    title: 'Upload too large',
    message: 'The complete upload exceeds the server limit.',
    action: 'Compress or split the dataset, or ask an administrator to raise the upload limit.',
    retryable: false,
  },
  STORAGE_CAPACITY_EXCEEDED: {
    title: 'Photogrammetry storage is full',
    message: 'This upload would exceed the configured storage limit or the server free-space reserve.',
    action: 'Delete an unused source-image folder or model, or ask a super administrator to increase storage capacity.',
    retryable: false,
  },
  UPLOAD_NETWORK_ERROR: {
    title: 'Upload connection lost',
    message: 'The browser could not complete the upload to the server.',
    action: 'Check the connection and retry. Large uploads are best sent as a ZIP archive.',
    retryable: true,
  },
  UPLOAD_CANCELLED: {
    title: 'Upload cancelled',
    message: 'The upload was cancelled before it completed.',
    action: 'Select the files and start the upload again when ready.',
    retryable: true,
  },
  TILESET_ZIP_REQUIRED: {
    title: 'ZIP archive required',
    message: 'No ZIP archive was received.',
    action: 'Choose a .zip file containing the complete 3D Tiles dataset.',
    retryable: true,
  },
  TILESET_ZIP_INVALID: {
    title: 'Invalid tileset archive',
    message: 'The archive is not a valid, safe 3D Tiles ZIP.',
    action: 'Verify the ZIP contains tileset.json and all referenced tile files, then retry.',
    retryable: false,
  },
  RECON_NO_IMAGES: {
    title: 'No drone images received',
    message: 'The photogrammetry upload did not contain supported JPEG images.',
    action: 'Choose a folder containing .jpg or .jpeg drone photographs.',
    retryable: true,
  },
  PHOTOGRAMMETRY_CAPACITY_EXCEEDED: {
    title: 'Photogrammetry worker is too small for this job',
    message: 'The selected dataset is expected to exceed the worker memory available for processing.',
    action: 'Choose a smaller dataset or Smoke-test preset, or ask a super administrator to use a larger photogrammetry worker.',
    retryable: false,
  },
  BAD_REQUEST: {
    title: 'Request could not be processed',
    message: 'Some submitted information is missing or invalid.',
    action: 'Review the entered values and retry.',
    retryable: true,
  },
  INTERNAL_ERROR: {
    title: 'Unexpected server error',
    message: 'The server could not complete the action.',
    action: 'Retry once. If it fails again, give the request ID to an administrator.',
    retryable: true,
  },
});

export function errorDefinition(code) {
  return ERROR_DEFINITIONS[code] || ERROR_DEFINITIONS.INTERNAL_ERROR;
}

export function problemBody(code, overrides = {}) {
  const definition = errorDefinition(code);
  return {
    code,
    title: overrides.title || definition.title,
    error: overrides.message || definition.message,
    message: overrides.message || definition.message,
    action: overrides.action || definition.action,
    retryable: overrides.retryable ?? definition.retryable,
    ...(overrides.requestId ? { requestId: overrides.requestId } : {}),
    ...(overrides.details ? { details: overrides.details } : {}),
    // Whether the caller can retry this exact operation with force. Carried
    // through because the client offers a different affordance for it — a
    // force-delete confirmation rather than a plain "try again", which cannot
    // help when the block is a state the operation will never leave on its own.
    ...(overrides.forceable ? { forceable: true } : {}),
  };
}
